<script setup lang="ts">
/**
 * =============================================================================
 * THEME SWITCHER COMPONENT
 * =============================================================================
 *
 * UI control for switching between theme presets and customizing colors.
 * Changes apply globally to all components using useTheme() composable.
 */

import { useTheme } from '@/composables/useTheme';

const theme = useTheme();
</script>

<template>
  <!-- Preset Selector -->
  <div class="mb-4">
    <label class="form-label fw-bold">Theme Presets</label>
    <div class="btn-group w-100" role="group" aria-label="Button hierarchy presets">
      <template v-for="preset in theme.presets" :key="preset.id">
        <input
          type="radio"
          class="btn-check"
          name="themePreset"
          :id="'preset-' + preset.id"
          :value="preset.id"
          :checked="theme.activePreset.value.id === preset.id"
          @change="theme.applyTheme(preset.id)"
          autocomplete="off"
        >
        <label
          class="btn btn-outline-primary"
          :for="'preset-' + preset.id"
        >
          {{ preset.name }}
        </label>
      </template>
    </div>
    <small class="text-muted d-block mt-1">
      {{ theme.activePreset.value.description }}
    </small>
  </div>

  <!-- Live Preview -->
  <div class="mb-4">
    <label class="form-label fw-bold">Preview</label>
    <div class="d-flex flex-wrap gap-2">
      <button class="btn" :class="theme.btn.value.primary" type="button">
        Primary CTA
      </button>
      <button class="btn" :class="theme.btn.value.secondary" type="button">
        Secondary
      </button>
      <button class="btn" :class="theme.btn.value.tertiary" type="button">
        Tertiary
      </button>
      <button class="btn" :class="theme.btn.value.danger" type="button">
        Danger
      </button>
      <button class="btn" :class="theme.btn.value.cancel" type="button">
        Cancel
      </button>
    </div>
  </div>

  <!-- Color Customization -->
  <div class="mb-3">
    <label class="form-label fw-bold">Custom Colors (Optional)</label>
    <div class="row g-2">
      <div class="col-md-4">
        <label class="form-label small">Primary</label>
        <input
          type="color"
          :value="theme.activePreset.value.colors.primary"
          @input="(e) => theme.updateColor('primary', (e.target as HTMLInputElement).value)"
          class="form-control form-control-color w-100"
        >
      </div>
      <div class="col-md-4">
        <label class="form-label small">Blackbox</label>
        <input
          type="color"
          :value="theme.activePreset.value.colors.blackbox"
          @input="(e) => theme.updateColor('blackbox', (e.target as HTMLInputElement).value)"
          class="form-control form-control-color w-100"
        >
      </div>
      <div class="col-md-4">
        <label class="form-label small">Danger</label>
        <input
          type="color"
          :value="theme.activePreset.value.colors.danger"
          @input="(e) => theme.updateColor('danger', (e.target as HTMLInputElement).value)"
          class="form-control form-control-color w-100"
        >
      </div>
    </div>
  </div>

  <!-- Reset Button -->
  <button
    class="bb-btn-cancel btn-sm"
    type="button"
    @click="theme.resetTheme()"
  >
    <i class="bi bi-arrow-counterclockwise me-1"></i>Reset to Default
  </button>
</template>
